import { IntakeReviewStepper } from "@/modules/third-party-risk/pages/intake-review/components/IntakeReviewStepper";
import { completeAssessmentIntake, fetchAssessmentTier, saveAssessmentIntakeQuestion, type AssessmentTierResponse } from "@/modules/third-party-risk/requests/intake-form-v2";
import { ZButton } from "@/shared/components/button";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";
import { addNotification } from "@/shared/states/notification";
import { yupResolver } from "@hookform/resolvers/yup";
import { Skeleton } from "@nextui-org/react";
import { AlertCircle, Loader } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useIntakeFormQuestions } from "../../hooks/useIntakeFormQuestions";
import { processFileForVendorAssessment } from "../../use-cases/vendor.use-case";
import type { IntakeFormQuestion } from "../../requests/vendor-intake-form-response";
import { useAgentSessionData } from "../../states";
import { useConditionalRequired } from "../../hooks/useConditionalRequired";
import { useIntakeFormStore } from "../../states/intake-form";
import { intakeFormValidationSchema } from "./intake-form.schema";
import { IntakeFormQuestionItem } from "./IntakeFormQuestionItem";

interface IdParams {
  id: string;
}

interface IntakeFormData {
  questions: IntakeFormQuestion[];
}

const AssessorSideIntakeForm: React.FC = React.memo(() => {
  const { id } = useParams<keyof IdParams>() as IdParams;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isViewMode = searchParams.get("mode") === "view";
  const sessiondata = useAgentSessionData(id);

  // Determine flow type: review flow (3 steps) vs normal flow (2 steps)
  const multiVendor = (sessiondata as any)?.multi_file_vendor_assessment;
  const intakeFormVersion = multiVendor?.intake_form_version;
  const businessOwnerFilledIntakeForm = multiVendor?.business_owner_filled_intake_form === true;
  const steps = ["Form", "Launch"];

  // Fetch questions from V2 API
  const { questions, isLoading, error, refetch } = useIntakeFormQuestions({ assessmentId: id });

  // Fetch tier data for subtitle badge (v2+ only)
  const [tierData, setTierData] = useState<AssessmentTierResponse | null>(null);
  useEffect(() => {
    if (intakeFormVersion === 1) return;
    fetchAssessmentTier(id)
      .then(setTierData)
      .catch(() => {
        // Graceful — tier badge simply won't show
      });
  }, [id, intakeFormVersion]);

  // State
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidQuestions, setInvalidQuestions] = useState<Set<string>>(new Set());
  const questionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [fileUploadErrors, setFileUploadErrors] = useState<Map<number, boolean>>(new Map());
  const pendingAutoSaveCallsRef = useRef<Set<string>>(new Set());
  const debounceTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const uploadingQuestionsRef = useRef<Set<number>>(new Set());

  // Get the store actions and state - define early so it can be used in callbacks
  const {
    isSaving: isIntakeFormSaving,
    isFormValid: isIntakeFormValid,
    isSubmitting: isIntakeFormSubmitting,
    setIsSaving: setStoreSaving,
    setIsFormValid,
    setIsSubmitting: setStoreSubmitting,
  } = useIntakeFormStore();

  // Track file upload completion delay timer - define early so it can be used in callbacks
  const fileUploadDelayTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize form with useFieldArray and Yup validation
  const formMethods = useForm<IntakeFormData>({
    mode: "onSubmit", // Only validate on submit
    reValidateMode: "onChange", // After first submit, re-validate on change to clear errors
    resolver: yupResolver(intakeFormValidationSchema) as any,
    defaultValues: {
      questions: [],
    },
  });

  const { handleSubmit, control, watch, reset, setValue, getValues } = formMethods;

  const { fields } = useFieldArray({
    control,
    name: "questions",
  });

  // Toggle child question `required` based on parent response
  useConditionalRequired({ watch, setValue });

  // Initialize with fetched questions and pre-fill existing responses
  useEffect(() => {
    if (questions.length > 0) {
      reset({
        questions: questions.map((q) => {
          // Pre-fill with existing response from backend, or use empty value
          let initialResponse;
          if (q.response !== null && q.response !== undefined) {
            // Use existing response from backend
            initialResponse = q.response;
          } else {
            // No existing response, use empty value based on type
            initialResponse = q.question_type === "multiple_choice" || q.question_type === "file_upload" ? [] : "";
          }

          return {
            ...q,
            response: initialResponse,
            saveFailed: false, // Initialize saveFailed flag
          };
        }),
      });
    }
  }, [questions, reset]);

  // Auto-save function without debounce (we'll handle debounce per-question)
  const autoSaveQuestion = useCallback(async (question: IntakeFormQuestion, questionIndex: number) => {
    const callId = `${question.id}-${Date.now()}`;

    try {
      // Add to pending calls
      pendingAutoSaveCallsRef.current.add(callId);
      setIsSaving(true);
      setStoreSaving(true);

      // For file_upload questions, move response URLs to source_urls
      let sourceUrls = question.source_urls || [];
      let response: string | string[] | null = question.response || "";

      if (question.question_type === "file_upload") {
        if (Array.isArray(question.response) && question.response.length > 0) {
          sourceUrls = question.response;
          response = "";
        }
      }

      await saveAssessmentIntakeQuestion(id, question.id, {
        response: response || "",
        source_urls: sourceUrls,
      });

      // Success - clear saveFailed flag if it was set
      setValue(`questions.${questionIndex}.saveFailed`, false);
    } catch (error) {
      console.error("Auto-save error:", error);

      // Mark this question as having failed save
      setValue(`questions.${questionIndex}.saveFailed`, true);
    } finally {
      // Remove from pending calls
      pendingAutoSaveCallsRef.current.delete(callId);

      // Only hide saving indicator if no pending calls, no uploads, and no pending delay
      if (pendingAutoSaveCallsRef.current.size === 0 && uploadingQuestionsRef.current.size === 0 && !fileUploadDelayTimerRef.current) {
        setIsSaving(false);
        setStoreSaving(false);
      }
    }
  }, [id, setValue, setStoreSaving]);

  // Debounced auto-save with per-question timer tracking
  const debouncedAutoSave = useCallback((question: IntakeFormQuestion, questionIndex: number) => {
    const questionId = question.id;

    // Clear existing timer for this specific question
    const existingTimer = debounceTimersRef.current.get(questionId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new timer for this question
    const timer = setTimeout(() => {
      void autoSaveQuestion(question, questionIndex);
      debounceTimersRef.current.delete(questionId);
    }, 1000);

    debounceTimersRef.current.set(questionId, timer);
  }, [autoSaveQuestion]);

  // Track previous response values to detect deletions
  const previousResponsesRef = useRef<Map<number, string[]>>(new Map());

  // Watch for changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name && name.startsWith("questions.") && name.endsWith(".response")) {
        const match = name.match(/questions\.(\d+)\.response/);
        if (match) {
          const index = parseInt(match[1], 10);
          const question = value.questions?.[index];
          if (question) {
            // For file_upload questions, check if this is a deletion (removing files)
            if (question.question_type === "file_upload" && Array.isArray(question.response)) {
              const previousResponse = previousResponsesRef.current.get(index) || [];
              // Filter out undefined values and ensure we have a clean string array
              const currentResponse = question.response.filter((url): url is string => typeof url === 'string');

              // Update the previous response tracker
              previousResponsesRef.current.set(index, currentResponse);

              // If current response is empty AND we had files before, this is a deletion - allow auto-save
              const isDeletion = currentResponse.length === 0 && previousResponse.length > 0;

              // Skip auto-save only for empty arrays that weren't deletions
              // This prevents premature API calls before files are fully uploaded
              if (currentResponse.length === 0 && !isDeletion) {
                return;
              }
            }
            void debouncedAutoSave(question as IntakeFormQuestion, index);
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, debouncedAutoSave]);

  // Scroll to first error
  const scrollToFirstError = useCallback(() => {
    const errors = formMethods.formState.errors;

    if (errors.questions && Array.isArray(errors.questions)) {
      // Find first question with error
      const firstErrorIndex = errors.questions.findIndex((q) => q !== undefined);
      if (firstErrorIndex >= 0 && fields[firstErrorIndex]) {
        const questionNumber = String(fields[firstErrorIndex].question_number);
        if (questionRefs.current.has(questionNumber)) {
          const element = questionRefs.current.get(questionNumber);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            setInvalidQuestions(new Set([questionNumber]));
            setTimeout(() => setInvalidQuestions(new Set()), 3000);
          }
        }
      }
    }
  }, [formMethods.formState.errors, fields]);

  const isSubmittingRef = useRef(false);

  const handleFormSubmit = async () => {
    // Prevent multiple simultaneous submissions
    if (isSubmittingRef.current) {
      return;
    }

    // Check if there are any file upload errors
    if (fileUploadErrors.size > 0) {
      // Find the first question with file upload error and scroll to it
      const firstErrorIndex = Array.from(fileUploadErrors.keys())[0];
      const firstErrorQuestion = fields[firstErrorIndex];

      if (firstErrorQuestion) {
        const questionNumber = String(firstErrorQuestion.question_number);
        if (questionRefs.current.has(questionNumber)) {
          const element = questionRefs.current.get(questionNumber);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            setInvalidQuestions(new Set([questionNumber]));
            setTimeout(() => setInvalidQuestions(new Set()), 3000);
          }
        }
      }

      addNotification({
        type: "error",
        message: "Upload Error: Please fix all file upload errors before submitting.",
      });
      setStoreSubmitting(false);
      return;
    }

    try {
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      setIsSaving(true);

      // Wait for all pending file uploads to complete
      // Poll every 100ms until no uploads remain
      // Add timeout to prevent infinite loop (max 5 minutes)
      const uploadStartTime = Date.now();
      const uploadTimeout = 5 * 60 * 1000; // 5 minutes

      while (uploadingQuestionsRef.current.size > 0) {
        if (Date.now() - uploadStartTime > uploadTimeout) {
          console.error("Upload timeout: File uploads took longer than 5 minutes");
          addNotification({
            type: "error",
            message: "File uploads are taking too long. Please check your connection and try again.",
          });
          throw new Error("Upload timeout");
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Wait for all pending auto-save calls to complete
      // Poll every 100ms until no pending calls remain
      // Add timeout to prevent infinite loop (max 2 minutes)
      const saveStartTime = Date.now();
      const saveTimeout = 2 * 60 * 1000; // 2 minutes

      while (pendingAutoSaveCallsRef.current.size > 0) {
        if (Date.now() - saveStartTime > saveTimeout) {
          console.error("Auto-save timeout: Save operations took longer than 2 minutes");
          addNotification({
            type: "error",
            message: "Saving is taking too long. Please check your connection and try again.",
          });
          throw new Error("Auto-save timeout");
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Get all questions from form
      const formData = formMethods.getValues();

      // Find all questions with saveFailed = true
      const failedQuestions = formData.questions
        .map((q, index) => ({ question: q, index }))
        .filter(({ question }) => question.saveFailed === true);

      if (failedQuestions.length > 0) {
        // Retry saving all failed questions (silently, without notification)
        const retryPromises = failedQuestions.map(async ({ question, index }) => {
          try {
            let sourceUrls = question.source_urls || [];
            let response: string | string[] | null = question.response || "";

            if (question.question_type === "file_upload") {
              if (Array.isArray(question.response) && question.response.length > 0) {
                sourceUrls = question.response;
                response = "";
              }
            }

            await saveAssessmentIntakeQuestion(id, question.id, {
              response: response || "",
              source_urls: sourceUrls,
            });

            // Mark as successfully saved
            setValue(`questions.${index}.saveFailed`, false);
          } catch (error) {
            // Silently catch errors - let the main API handle validation errors
            console.error(`Failed to save question ${question.question_number}:`, error);
          }
        });

        // Wait for all retries to complete (but don't fail if they don't succeed)
        await Promise.allSettled(retryPromises);
      }

      if (intakeFormVersion === 1) {
        // v1: directly start assessment, skip tiering/launch
        await processFileForVendorAssessment(id, navigate);
      } else {
        // v2+: complete intake then navigate to Tiering step
        await completeAssessmentIntake(id);
        setSearchParams(prev => { prev.set("reviewStep", "2"); return prev; });
      }
    } catch (error) {
      console.error("Failed to validate form:", error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      setIsSaving(false);
      setStoreSubmitting(false);
    }
  }

  const handleFormErrors = () => {
    // Turn off loader when validation fails
    setStoreSubmitting(false);
    scrollToFirstError();
    addNotification({
      type: "error",
      message: "Please fill in all the required fields",
    });
  }

  // Wrapper to show loader immediately on form submission
  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Show loader immediately when form is submitted
    setStoreSubmitting(true);
    // Let react-hook-form handle the rest
    handleSubmit(handleFormSubmit, handleFormErrors)(e);
  };

  const clearInvalidQuestion = useCallback((questionKey: string) => {
    setInvalidQuestions((prev) => {
      const newSet = new Set(prev);
      newSet.delete(questionKey);
      return newSet;
    });
  }, []);

  const handleFileUploadError = useCallback((questionIndex: number, hasError: boolean) => {
    setFileUploadErrors((prev) => {
      const newMap = new Map(prev);
      if (hasError) {
        newMap.set(questionIndex, true);
      } else {
        newMap.delete(questionIndex);
      }
      return newMap;
    });
  }, []);

  const handleFileUploadStatusChange = useCallback((questionIndex: number, isUploading: boolean) => {
    if (isUploading) {
      // Clear any pending delay timer when a new upload starts
      if (fileUploadDelayTimerRef.current) {
        clearTimeout(fileUploadDelayTimerRef.current);
        fileUploadDelayTimerRef.current = null;
      }
      uploadingQuestionsRef.current.add(questionIndex);
      setIsSaving(true);
      setStoreSaving(true);
    } else {
      uploadingQuestionsRef.current.delete(questionIndex);
      // Only start the delay if no more uploads are in progress
      if (uploadingQuestionsRef.current.size === 0) {
        // Add 2-second delay after file upload completes before allowing submission
        fileUploadDelayTimerRef.current = setTimeout(() => {
          // Only hide saving indicator if no pending auto-save calls
          if (pendingAutoSaveCallsRef.current.size === 0) {
            setIsSaving(false);
            setStoreSaving(false);
          }
          fileUploadDelayTimerRef.current = null;
        }, 2500);
      }
    }
  }, [setStoreSaving]);

  // Update form validity in store when form state changes
  useEffect(() => {
    const hasErrors = Object.keys(formMethods.formState.errors).length > 0;
    const hasFileUploadErrors = fileUploadErrors.size > 0;
    const isCurrentlyUploading = uploadingQuestionsRef.current.size > 0;
    const hasPendingDelay = fileUploadDelayTimerRef.current !== null;
    
    setIsFormValid(!hasErrors && !hasFileUploadErrors && !isCurrentlyUploading && !hasPendingDelay && !isSaving);
  }, [formMethods.formState.errors, fileUploadErrors, isSaving, setIsFormValid]);

  // Reset form validity on mount and cleanup on unmount
  useEffect(() => {
    // Start with form invalid — the validation effect will set the correct value once the form settles
    setIsFormValid(false);

    return () => {
      if (fileUploadDelayTimerRef.current) {
        clearTimeout(fileUploadDelayTimerRef.current);
      }
      // Reset store state when component unmounts to prevent stale state in other sessions
      setStoreSaving(false);
      setStoreSubmitting(false);
      setIsFormValid(true);
    };
  }, [setStoreSaving, setStoreSubmitting, setIsFormValid]);

  if (isLoading) {
    return (
      <div className="w-full h-full bg-[#FAFAFA] bg-opacity-90 flex flex-col items-center">
        {/* Header skeleton */}
        <div className="flex items-center p-4 border-b border-[#E2E8F0] w-full bg-white">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Skeleton className="w-10 h-10 rounded-full" />
            <Skeleton className="w-48 h-7 rounded" />
          </div>
          <Skeleton className="w-64 h-6 rounded" />
          <div className="flex-1" />
        </div>

        {/* Content skeleton */}
        <div className="px-16 py-4 flex-1 w-full overflow-y-auto">
          <div className="bg-[#FEFEFE] w-full rounded-lg p-6 flex flex-col gap-6">
            {/* Title + tier badge */}
            <div className="flex flex-row items-center gap-6 self-stretch">
              <div className="flex flex-col items-start gap-2 flex-1">
                <Skeleton className="w-56 h-5 rounded" />
                <Skeleton className="w-96 h-4 rounded" />
              </div>
              <Skeleton className="w-32 h-10 rounded-lg" />
            </div>

            {/* Question skeletons */}
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Skeleton className="w-6 h-4 rounded" />
                  <Skeleton className={`h-4 rounded ${i % 2 === 0 ? "w-72" : "w-56"}`} />
                </div>
                <Skeleton className={`w-full rounded ${i % 3 === 0 ? "h-20" : "h-10"}`} />
              </div>
            ))}

            {/* Continue button */}
            <Skeleton className="w-24 h-10 rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full bg-[#FAFAFA] bg-opacity-90 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 bg-white border border-[#E4E4E7] rounded-lg p-8 max-w-md">
          <AlertCircle className="w-10 h-10 text-danger" />
          <span className="text-sm text-[#64748B] text-center">
            {error.message || "Failed to load intake form questions. Please try again."}
          </span>
          <ZButton variant="bordered" size="sm" onPress={() => refetch()}>
            Retry
          </ZButton>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full bg-[#FAFAFA] bg-opacity-90 flex flex-col items-center relative ${isSubmitting ? 'pointer-events-none' : ''}`}>
      {/* Overlay during submission */}
      {isSubmitting && (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50">
          <div className="flex flex-col items-center gap-3">
            <LoadingSpinner
              containerClassName="bg-transparent relative"
              className="mx-auto"
            />
            {/* <p className="text-sm text-gray-600 font-medium">Submitting your responses...</p> */}
          </div>
        </div>
      )}
      <div className="flex items-center p-4 border-b border-[#E2E8F0] w-full bg-white">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <VendorLogo
            alt={(sessiondata as any).multi_file_vendor_assessment?.vendor_name || ""}
            src={(sessiondata as any).multi_file_vendor_assessment?.vendor_logo_url || ""}
            needsAuthToken
            className="border border-[#E2E8F0]"
          />
          <span className="font-semibold text-2xl leading-7 text-[#020618] truncate">
            {(sessiondata as any).multi_file_vendor_assessment?.vendor_name}{" "}
            {(sessiondata as any).multi_file_vendor_assessment?.product_name || ""}
          </span>
        </div>

        {intakeFormVersion !== 1 && <IntakeReviewStepper currentStep={1} steps={steps} />}

        <div className="flex items-center justify-end gap-4 flex-1 min-w-0">
          {/* Only show auto-save indicator when not in view mode */}
          {!isViewMode && (
            <div className="flex items-center h-7 gap-1">
              <Loader className={`w-4 h-4 animate-spin ${isSaving ? "" : "invisible"}`} />
              {isSaving && <p className="text-tiny text-[#A5A5A5] text-[10px]">Auto-saving</p>}
            </div>
          )}
        </div>
      </div>
      <div className="px-16 py-4 flex-1 w-full overflow-y-auto">
        <div className="bg-[#FEFEFE] w-full rounded-lg p-6 flex flex-col gap-6">
          {intakeFormVersion !== 1 && (
            <div className="flex flex-row items-center gap-6 self-stretch">
              <div className="flex flex-col items-start gap-2 flex-1">
                <span className="font-semibold text-base leading-6 text-[#0F172B] self-stretch">
                  {businessOwnerFilledIntakeForm
                    ? "Review Intake Form Responses"
                    : "Complete Intake Form"}
                </span>
                <span className="text-sm leading-5 text-[#62748E] self-stretch">
                  {businessOwnerFilledIntakeForm
                    ? "Verify the intake details and complete any remaining fields to proceed to tiering."
                    : "Provide third-party context and describe its use, including relevant documents"}
                </span>
              </div>
              {businessOwnerFilledIntakeForm && tierData && (
                <div className="flex flex-col items-start px-4 py-3 bg-[#EFF6FF] border border-[#BEDBFF] rounded-lg">
                  <div className="flex flex-row items-center gap-4">
                    <span className="text-xs font-medium leading-5 text-[#62748E]">
                      Calculated Tier
                    </span>
                    <span className="flex items-baseline px-3 py-0.5 bg-white rounded-full">
                      <span className="font-medium text-sm leading-5">
                        Tier {tierData.tier_number}
                      </span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          <FormProvider {...formMethods}>
            <form className="flex flex-col gap-6" id="assessor-intake-form" onSubmit={onFormSubmit}>
              {fields.map((field, index) => (
                <IntakeFormQuestionItem
                  key={field.id}
                  index={index}
                  isInvalid={invalidQuestions.has(String(field.question_number))}
                  onFocus={clearInvalidQuestion}
                  questionRef={questionRefs}
                  onFileUploadError={handleFileUploadError}
                  onFileUploadStatusChange={handleFileUploadStatusChange}
                  isViewMode={isViewMode}
                />
              ))}
              {/* Continue button below the last question */}
              {!isViewMode && fields.length > 0 && (
                <div className="pt-2">
                  <ZButton
                    type="submit"
                    form="assessor-intake-form"
                    isDisabled={isIntakeFormSaving || !isIntakeFormValid || isIntakeFormSubmitting}
                    isLoading={isIntakeFormSubmitting}
                    className="h-10 px-4 py-2 gap-1.5 rounded-md text-base font-medium"
                  >
                    Continue
                  </ZButton>
                </div>
              )}
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
});

AssessorSideIntakeForm.displayName = "AssessorSideIntakeForm";

export default AssessorSideIntakeForm;
